/**
 * Intro animation for the Home Hero (Views/Partials/components/homeHero.cshtml).
 *
 * Plays once on load, in three beats: the heading's words scale down into place, then the intro
 * copy and the button slide down together as a single block, then the image slides up.
 *
 * The markup ships visible — the starting state is applied here with GSAP rather than in CSS, so
 * a script that never loads leaves a readable hero instead of a blank blue band.
 */

import { gsap } from "gsap";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(SplitText);

function initHomeHero(root) {
    const heading = root.querySelector("[data-home-hero-heading]");
    const copy = Array.from(root.querySelectorAll("[data-home-hero-copy]"));
    const image = root.querySelector("[data-home-hero-image]");
    const targets = [heading, ...copy, image].filter(Boolean);

    // Leave everything in its final state when the visitor has asked for less motion.
    if (targets.length === 0 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
    }

    // Hide straight away, before waiting on fonts below, so the hero isn't painted in full and
    // then yanked back to the start of the animation.
    gsap.set(targets, { opacity: 0 });

    // Splitting before the webfont lands would measure the fallback face and leave the words in
    // the wrong places, so hold until the font is resolved.
    document.fonts.ready.then(() => {
        const split = heading ? new SplitText(heading, { type: "words" }) : null;

        const timeline = gsap.timeline({
            defaults: { ease: "power3.out" },
            onComplete: () => {
                // Put the heading back to plain text so selection, in-page find and screen
                // readers aren't left picking through per-word spans.
                split?.revert();
                gsap.set(targets, { clearProps: "opacity,transform" });
            },
        });

        if (split) {
            timeline.fromTo(
                split.words,
                // inline-block: transforms have no effect on inline boxes, so scale needs it.
                { opacity: 0, scale: 1.3, display: "inline-block" },
                { opacity: 1, scale: 1, duration: 0.7, stagger: 0.06 },
            );

            // The words carry the reveal now, so the heading itself can come back.
            timeline.set(heading, { opacity: 1 }, 0);
        }

        if (copy.length > 0) {
            // One tween across both elements with no stagger: they travel as a single unit.
            timeline.fromTo(
                copy,
                { opacity: 0, y: -24 },
                { opacity: 1, y: 0, duration: 0.6 },
            );
        }

        if (image) {
            timeline.fromTo(
                image,
                { opacity: 0, y: 60 },
                { opacity: 1, y: 0, duration: 0.8 },
            );
        }
    });
}

document.querySelectorAll("[data-home-hero]").forEach(initHomeHero);
